import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Divider,

} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';

// Dummy chat history data
const DUMMY_CHAT_CONTEXTS = [
  {
    id: '1',
    name: 'Market Analysis Q1 2024',
    lastUpdated: '2024-01-15T10:30:00Z',
    messageCount: 12,
    lastMessage: 'What are the top trends in the snack food market?',
    createdAt: '2024-01-10T09:00:00Z',
  },
  {
    id: '2',
    name: 'Product Development Ideas',
    lastUpdated: '2024-01-14T15:45:00Z',
    messageCount: 8,
    lastMessage: 'Can you suggest some healthy soup variants?',
    createdAt: '2024-01-12T14:20:00Z',
  },
  {
    id: '3',
    name: 'Competitor Research',
    lastUpdated: '2024-01-13T11:20:00Z',
    messageCount: 15,
    lastMessage: 'How is Heinz performing in the organic segment?',
    createdAt: '2024-01-08T16:15:00Z',
  },
  {
    id: '4',
    name: 'Consumer Insights Study',
    lastUpdated: '2024-01-12T14:10:00Z',
    messageCount: 6,
    lastMessage: 'What do millennials prefer in ready-to-eat meals?',
    createdAt: '2024-01-11T11:30:00Z',
  },
];

const ChatContextManager = ({ 
  open, 
  onClose, 
  onSelectContext, 
  currentContextId,
  onCreateContext,
  onUpdateContext,
  onDeleteContext 
}) => {
  const [contexts, setContexts] = useState(DUMMY_CHAT_CONTEXTS);
  const [selectedContext, setSelectedContext] = useState(currentContextId);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuContextId, setMenuContextId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContext, setEditingContext] = useState(null);
  const [newContextName, setNewContextName] = useState('');

  // Format relative time
  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  // Handle context menu
  const handleMenuOpen = (event, contextId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuContextId(contextId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuContextId(null);
  };

  // Handle context selection with option to unselect
  const handleSelectContext = (contextId) => {
    // If clicking the same context that's already selected, unselect it
    if (currentContextId === contextId) {
      setSelectedContext(null);
      onSelectContext?.(null);
    } else {
      setSelectedContext(contextId);
      onSelectContext?.(contextId);
    }
    onClose();
  };

  // Handle create new context
  const handleCreateContext = () => {
    const newContext = {
      id: uuidv4(),
      name: newContextName || `New Chat ${contexts.length + 1}`,
      lastUpdated: new Date().toISOString(),
      messageCount: 0,
      lastMessage: '',
      createdAt: new Date().toISOString(),
    };
    
    setContexts([newContext, ...contexts]);
    setNewContextName('');
    onCreateContext?.(newContext);
    handleSelectContext(newContext.id);
  };

  // Handle edit context
  const handleEditContext = () => {
    const context = contexts.find(c => c.id === menuContextId);
    setEditingContext(context);
    setNewContextName(context.name);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveEdit = () => {
    const updatedContexts = contexts.map(c => 
      c.id === editingContext.id 
        ? { ...c, name: newContextName, lastUpdated: new Date().toISOString() }
        : c
    );
    setContexts(updatedContexts);
    setEditDialogOpen(false);
    setEditingContext(null);
    setNewContextName('');
    onUpdateContext?.(editingContext.id, newContextName);
  };

  // Handle delete context
  const handleDeleteContext = () => {
    const updatedContexts = contexts.filter(c => c.id !== menuContextId);
    setContexts(updatedContexts);
    
    // If deleted context was selected, select the first available context
    if (selectedContext === menuContextId && updatedContexts.length > 0) {
      handleSelectContext(updatedContexts[0].id);
    }
    
    onDeleteContext?.(menuContextId);
    handleMenuClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon color="primary" />
              <Typography variant="h6">Chat History</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Clear selection button */}
              {currentContextId && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleSelectContext(null)}
                  sx={{ mr: 1 }}
                >
                  Clear Selection
                </Button>
              )}
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={handleCreateContext}
                size="small"
              >
                New Chat
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ flex: 1, p: 0 }}>
          {/* Add "No Context" option at the top */}
          <List sx={{ pt: 0 }}>
            <ListItem
              button
              selected={currentContextId === null}
              onClick={() => handleSelectContext(null)}
              sx={{
                py: 2,
                borderBottom: '1px solid #e0e0e0',
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Default Chat (No Context)
                    </Typography>
                    <Chip
                      label="Current Session"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  </Box>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary">
                    Continue with current chat without specific context
                  </Typography>
                }
              />
            </ListItem>

            {contexts.length === 0 ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="200px"
                gap={2}
              >
                <ChatIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                <Typography variant="h6" color="text.secondary">
                  No saved chat contexts yet
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Create a new chat context to save and organize your conversations
                </Typography>
              </Box>
            ) : (
              contexts.map((context, index) => (
                <React.Fragment key={context.id}>
                  <ListItem
                    button
                    selected={selectedContext === context.id}
                    onClick={() => handleSelectContext(context.id)}
                    sx={{
                      py: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'primary.light',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {context.name}
                          </Typography>
                          <Chip
                            label={`${context.messageCount} messages`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                          {currentContextId === context.id && (
                            <Chip
                              label="Active"
                              size="small"
                              color="primary"
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {context.lastMessage && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mb: 0.5,
                              }}
                            >
                              {context.lastMessage}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(context.lastUpdated)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={(e) => handleMenuOpen(e, context.id)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < contexts.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditContext}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleDeleteContext} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rename Chat</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Chat Name"
            value={newContextName}
            onChange={(e) => setNewContextName(e.target.value)}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ChatContextManager.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSelectContext: PropTypes.func,
  currentContextId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCreateContext: PropTypes.func,
  onUpdateContext: PropTypes.func,
  onDeleteContext: PropTypes.func
};

export default ChatContextManager;
